import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type CataloguePillar } from '../hooks/useServiceCatalogue';

type Props = {
  pillars: readonly CataloguePillar[];
  /** The pillar the reader has reached, or undefined before the first frame. */
  activeId: string | undefined;
};

/**
 * The catalogue's own table of contents: three stages, the current one marked.
 *
 * Real anchors, not buttons calling `scrollIntoView`. The band is prerendered,
 * so on a cold load the script has not arrived yet and a button would do
 * nothing; an `href="#id"` is scrolled by the browser itself and works before
 * any JavaScript runs. It is also a link, which is what it is — the keyboard
 * and the context menu both behave correctly for free.
 *
 * `activeId` is `undefined` until the first frame after mount (see
 * `useScrollSpy`), which is deliberate rather than a gap: the prerendered HTML
 * ships with no pillar marked current, because nobody has scrolled it yet.
 */
export function ServicePillarRail({ pillars, activeId }: Props) {
  return (
    <Stack
      component="nav"
      aria-label="Service groups"
      spacing={1}
      // Sticky, and `alignItems` is deliberately not set anywhere up this
      // tree: a flex parent that stretches or centres its children gives this
      // column a height equal to the row, and `position: sticky` has nothing
      // left to travel inside. See the sticky-column note in the repo.
      sx={{ position: { lg: 'sticky' }, top: { lg: 136 } }}
    >
      {pillars.map((pillar) => {
        const active = activeId === pillar.anchorId;
        return (
          <Box
            key={pillar.id}
            component="a"
            href={`#${pillar.anchorId}`}
            aria-current={active ? 'true' : undefined}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 3,
              alignItems: 'baseline',
              px: 4,
              py: 3,
              borderRadius: 2,
              textDecoration: 'none',
              color: active ? 'text.primary' : 'text.secondary',
              bgcolor: active ? 'surfaceVariant' : 'transparent',
              borderLeft: 2,
              borderColor: active ? 'primary.main' : 'transparent',
              transition: 'color 200ms ease, background-color 200ms ease, border-color 200ms ease',
              '&:hover': { color: 'text.primary', bgcolor: 'surfaceVariant' },
            }}
          >
            <Typography
              aria-hidden
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: active ? 'primary.main' : 'text.disabled',
              }}
            >
              {pillar.step}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: active ? 700 : 600 }}>
              {pillar.title}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}
